import { useOrganizations } from '@blend-ed/blendxplatform/hooks';
import {
  Alert, Button, Card, Container, Icon, Image, Pagination, SearchField,
} from '@openedx/paragon';
import LoadingView from '../../containers/Dashboard/LoadingView';
import { Groups, InfoOutline } from '@openedx/paragon/icons';
import { getConfig } from '@edx/frontend-platform';
import { getCookies } from '@edx/frontend-platform/i18n/lib';
import { FormattedMessage, injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import messages from './messages';

import './index.scss';
import { RiSearch2Line } from '@remixicon/react';

const ITEMS_PER_PAGE = 6;
const DEBOUNCE_MS = 300;

// Pull the org slug out of a `next` URL that points at a single course page,
// e.g. `/dashboard/course/course-v1:MyOrg+CS101+2024` -> `MyOrg`.
const extractOrgFromCourseUrl = (url) => {
  if (!url) { return null; }
  try {
    const courseUrlMatch = url.match(/\/dashboard\/course\/(.+)$/);
    if (!courseUrlMatch) { return null; }
    const courseId = decodeURIComponent(courseUrlMatch[1]);
    const courseIdMatch = courseId.match(/^course-v1:([^+\s]+)/);
    return courseIdMatch ? courseIdMatch[1] : null;
  } catch {
    return null;
  }
};

const OrganizationsInner = ({ intl }) => {
  const navigate = useNavigate();
  const next = new URLSearchParams(window.location.search).get('next');
  const [search, setSearch] = useState('');
  const [noMatchingOrgMessage, setNoMatchingOrgMessage] = useState('');
  const debounceRef = useRef(null);

  const { data: orgList, loading } = useOrganizations();

  const {
    MINIO_BASE_URL, LMS_BASE_URL, MINIO_BUCKET_NAME, ORGANISATION_LOGO_PATH, SUPPORT_URL, LOGOUT_URL, INFO_EMAIL,
  } = getConfig();
  const orgName = getCookies().get('orgName');

  // Contact-support target: prefer an explicitly configured SUPPORT_URL,
  // otherwise email the platform support address (INFO_EMAIL, same source
  // the authn MFE uses for its support links), then fall back to the LMS
  // contact page. The LMS `/support` route is the staff-only support tools
  // dashboard (403 for learners), so it must not be used here.
  const supportHref = SUPPORT_URL
    || (INFO_EMAIL ? `mailto:${INFO_EMAIL}` : null)
    || (LMS_BASE_URL ? `${LMS_BASE_URL.replace(/\/$/, '')}/contact` : null);

  const [currentPage, setCurrentPage] = useState(0);

  const handleSearchChange = useCallback((value) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearch(value);
    }, DEBOUNCE_MS);
  }, []);

  const handleSearchClear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setSearch('');
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  const filteredOrgs = orgList?.filter((org) => org._Organisations.name.toLowerCase().includes(search.toLowerCase()));

  const totalPages = Math.ceil((filteredOrgs?.length || 0) / ITEMS_PER_PAGE);
  const paginatedOrgs = filteredOrgs?.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE,
  );

  const handleOrganizationClick = (org) => {
    try {
      const domain = `.${LMS_BASE_URL.split('://')[1].split(':')[0]}`;
      getCookies().set('orgName', org.name, { path: '/', domain });
    } catch {
      getCookies().set('orgName', org.name, { path: '/' });
    }

    // If we were showing a "no matching org" warning, the original `next`
    // pointed at a course the user can't access — send them home instead.
    if (noMatchingOrgMessage) {
      navigate('/', { replace: true });
    } else if (next) {
      const fixedNext = next.replace(/ /g, '+');
      window.location.href = fixedNext;
    } else {
      navigate('/', { replace: true });
    }
  };

  // Clear the orgName cookie on logout so the next login re-runs org selection
  // instead of silently reusing a stale org. Mirrors the sidebar's logout.
  const handleLogout = () => {
    try {
      const domain = `.${LMS_BASE_URL.split('://')[1].split(':')[0]}`;
      getCookies().remove('orgName', { path: '/', domain });
    } catch {
      getCookies().remove('orgName', { path: '/' });
    }
    window.location.href = LOGOUT_URL || '#';
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [search]);

  // Highest-priority auto-select: `next` points at a single course page and
  // we can infer the org from the course id. Use that org if it's available
  // to the user, otherwise surface a message so they can pick manually.
  useEffect(() => {
    if (loading || !orgList?.length) { return; }
    const extractedOrgName = extractOrgFromCourseUrl(next);
    if (!extractedOrgName) { return; }

    const matchingOrg = orgList.find((org) => org.name === extractedOrgName);
    if (matchingOrg) {
      handleOrganizationClick(matchingOrg);
    } else {
      setNoMatchingOrgMessage(
        intl.formatMessage(messages.noMatchingOrganization, { orgName: extractedOrgName }),
      );
    }
  }, [loading, orgList, next]);

  useEffect(() => {
    if (loading) { return; }
    // Don't auto-redirect if we're telling the user their course org isn't
    // available — they need to see the picker + alert.
    if (noMatchingOrgMessage) { return; }
    // Skip if a course-url auto-select is about to run.
    if (extractOrgFromCourseUrl(next)) { return; }

    // Single org available — pick it for the user (re-sets cookie + routes).
    if (orgList?.length === 1) {
      handleOrganizationClick(orgList[0]);
      return;
    }

    // Already chose an org previously and a stale link bounced us here with
    // a `next`. Honour the existing selection — but only if it's still a real
    // org for this user. Trusting the cookie blindly lets an invalid orgName
    // (e.g. an org the admin was never granted) ping-pong between here and the
    // authoring MFE forever, so clear it and fall through to the picker.
    if (next && orgName) {
      if (orgList.some((org) => org.name === orgName)) {
        window.location.href = next.replace(/ /g, '+');
      } else {
        try {
          const domain = `.${LMS_BASE_URL.split('://')[1].split(':')[0]}`;
          getCookies().remove('orgName', { path: '/', domain });
        } catch {
          getCookies().remove('orgName', { path: '/' });
        }
      }
    }
  }, [loading, orgList, noMatchingOrgMessage]);

  // Derived synchronously so the very first frame after `loading` flips shows
  // the loader instead of the org picker.
  const isAutoRedirecting = Boolean(orgList?.length) && !noMatchingOrgMessage && (
    (orgList?.length === 1)
    || Boolean(extractOrgFromCourseUrl(next))
    || Boolean(next && orgName && orgList?.some((org) => org.name === orgName))
  );

  if (loading || isAutoRedirecting) {
    return (
      <main className="organizations-page">
        <Container className="organizations-container d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
          <LoadingView />
        </Container>
      </main>
    );
  }

  // The user authenticated successfully but belongs to no organization (e.g.
  // they were removed from every org they were in). Show a clear explanation
  // and a path forward instead of an empty picker they can't act on.
  if (!orgList?.length) {
    return (
      <main className="organizations-page">
        <Container className="organizations-container organizations-container--centered">
          <div className="organizations-no-membership">
            <Icon src={InfoOutline} className="organizations-no-membership-icon" />
            <h6 className="organizations-no-membership-title">
              <FormattedMessage {...messages.noMembershipTitle} />
            </h6>
            <p className="organizations-no-membership-text">
              <FormattedMessage {...messages.noMembershipText} />
            </p>
            <div className="organizations-no-membership-actions">
              {supportHref && (
                <Button
                  variant="primary"
                  href={supportHref}
                  {...(!supportHref.startsWith('mailto:') && { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  <FormattedMessage {...messages.contactSupport} />
                </Button>
              )}
              <Button
                variant={supportHref ? 'outline-primary' : 'primary'}
                onClick={handleLogout}
              >
                <FormattedMessage {...messages.logout} />
              </Button>
            </div>
          </div>
        </Container>
      </main>
    );
  }

  const getLogoUrl = (name) => {
    if (MINIO_BASE_URL) {
      return `${MINIO_BASE_URL}/${MINIO_BUCKET_NAME}/${ORGANISATION_LOGO_PATH}${name}/logo.png`;
    }
    return `${LMS_BASE_URL}/media/organisation-logos/${name}/logo.png`;
  };

  return (
    <main className="organizations-page">
      <Container className="organizations-container">
        <div className="organizations-content">
          <div className="organizations-sticky-header">
            <h6 className="organizations-title">
              <FormattedMessage {...messages.selectOrganization} />
            </h6>

            {noMatchingOrgMessage && (
              <Alert variant="danger" className="organizations-alert">
                {noMatchingOrgMessage}
              </Alert>
            )}

            <SearchField.Advanced
              onSubmit={() => {}}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
              className="organizations-search"
            >
              <RiSearch2Line className="pgn__searchfield__search-icon" />
              <SearchField.Input placeholder={intl.formatMessage(messages.searchPlaceholder)} />
              <SearchField.ClearButton />
            </SearchField.Advanced>
          </div>

          <div className="organizations-scroll-area">
            {filteredOrgs?.length === 0 && (
              <div className="organizations-empty">
                <FormattedMessage {...messages.noOrganizations} />
              </div>
            )}

            <div className="organizations-grid">
              {paginatedOrgs?.map((org) => (
                <Card
                  key={org.id}
                  className="organization-card"
                  isClickable
                  onClick={() => handleOrganizationClick(org)}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleOrganizationClick(org)}
                >
                  <Card.Body>
                    <div className="organization-card-content">
                      <div className="organization-logo-wrapper">
                        <Image
                          src={getLogoUrl(org.name)}
                          alt={org._Organisations.name}
                          className="organization-logo"
                        />
                      </div>
                      <div className="organization-name-wrapper">
                        <span className="organization-name">{org._Organisations.name}</span>
                      </div>
                    </div>
                  </Card.Body>
                  {org.memberCount !== undefined && (
                    <div className="organization-member-badge">
                      <Icon src={Groups} className="member-icon" />
                      <span className="member-count">{org.memberCount}</span>
                    </div>
                  )}
                </Card>
              ))}
            </div>

          </div>

          {totalPages > 1 && (
            <div className="organizations-pagination">
                <Pagination
                  paginationLabel={intl.formatMessage(messages.paginationLabel)}
                  pageCount={totalPages}
                  currentPage={currentPage + 1}
                  onPageSelect={(page) => setCurrentPage(page - 1)}
                  buttonLabels={{
                    previous: '',
                    next: '',
                    page: intl.formatMessage(messages.paginationPage),
                    currentPage: intl.formatMessage(messages.paginationCurrentPage),
                    pageOfCount: intl.formatMessage(messages.paginationPageOfCount),
                  }}
                />
            </div>
          )}
        </div>
      </Container>
    </main>
  );
};

OrganizationsInner.propTypes = {
  intl: intlShape.isRequired,
};

const Organizations = injectIntl(OrganizationsInner);

export default Organizations;
